import { apiClient } from './client'
import type { ApiResponse, PageResponse } from '@/types/api'

export interface LcOperationsOrganization {
  organizationId: number
  name: string
  displayName: string | null
  status: string
  isActive: boolean
}

export interface LcOperationsSummary {
  organization: LcOperationsOrganization
  classGroups: {
    active: number
    inactive: number
  }
  learners: {
    enrolled: number
    onLeave: number
    waiting: number
    withdrawn: number
  }
  reviewRequired: number
  inspectedAt: string
}

export type LcClosureState = 'SCHEDULED' | 'ONGOING' | 'ENDED'

export interface LcOperationsClosure {
  closureId: number
  startDate: string
  endDate: string | null
  reason: string | null
  state: LcClosureState
  createdAt: string
  updatedAt: string
}

export interface LcOperationsClassGroup {
  timeslotId: number
  name: string
  description: string | null
  isActive: boolean
  teacherUserId: number | null
  teacherName: string | null
  schedule: string
  weeklySessionCount: number
  maxStudents: number | null
  currentStudents: number
  onLeaveStudents: number
  currentlyClosed: boolean
  closures: LcOperationsClosure[]
  createdAt: string
  updatedAt: string
}

export interface LcOperationsRosterItem {
  enrollmentId: number
  userId: number
  fullName: string
  gradeLevel: string | null
  guardianPhoneLast4: string | null
  learnerStatus: string
  rawEnrollmentStatus: string
  current: boolean
  trialStatus: string | null
  courseName: string | null
  currentCurriculum: string | null
  completionRate: number | null
  unusedLessons: number | null
  learningEndDate: string | null
  enrollmentEndDate: string | null
}

export interface LcOperationsClassGroupQuery {
  includeInactive: boolean
  query: string
  sort: 'name' | 'status' | 'createdAt' | 'updatedAt'
  direction: 'asc' | 'desc'
  page: number
  size: number
}

export interface LcOperationsIntegrityWarning {
  code: string
  userId: number
  enrollmentId: number | null
  message: string
}

export interface LcOperationsLearner {
  userId: number
  fullName: string
  handle: string
  gradeLevel: string | null
  gender: string | null
  learnerStatus: string
  accountActive: boolean
  courseName: string | null
  courseType: 'FREE_TRIAL' | 'REGULAR' | null
  classGroupName: string | null
  contentLevel: string | null
  contentPaidAt: string | null
  grantedLessons: number | null
  usedLessons: number | null
  remainingLessons: number | null
  diagnosticStatus: string | null
  registeredAt: string
  learningStartDate: string | null
  learningEndDate: string | null
  tenureDays: number | null
  primaryEnrollmentId: number | null
  integrityWarnings: string[]
}

export interface LcOperationsLearnerPage {
  content: LcOperationsLearner[]
  page: number
  size: number
  totalElements: number
  totalPages: number
  first: boolean
  last: boolean
  totalsByStatus: Record<string, number>
  integrityWarnings: LcOperationsIntegrityWarning[]
}

export interface LcOperationsLearnerQuery {
  query: string
  status: 'ENROLLED' | 'ON_LEAVE' | 'WAITING' | 'WITHDRAWN' | null
  courseType: 'FREE_TRIAL' | 'REGULAR' | null
  sort: 'name' | 'gradeLevel' | 'registeredAt' | 'status'
  direction: 'asc' | 'desc'
  page: number
  size: number
}

export interface LcOperationsGuardian {
  name: string | null
  phone: string | null
  postalCode: string | null
  addressBase: string | null
  addressDetail: string | null
}

export interface LcOperationsLearnerOverview {
  organizationId: number
  userId: number
  fullName: string
  displayName: string | null
  handle: string
  email: string
  phoneNumber: string | null
  countryCode: string | null
  dateOfBirth: string | null
  gender: string | null
  school: string | null
  country: string | null
  state: string | null
  city: string | null
  addressLine1: string | null
  addressLine2: string | null
  postalCode: string | null
  gradeLevel: string | null
  guardian: LcOperationsGuardian | null
  accountActive: boolean
  membershipStatus: string
  joinedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface LcOperationsEnrollment {
  enrollmentId: number
  userId: number
  licenseId: number
  timeslotId: number | null
  rawStatus: string
  courseName: string | null
  licenseActive: boolean | null
  licenseValidFrom: string | null
  licenseValidUntil: string | null
  classGroupName: string | null
  classGroupActive: boolean | null
  startDate: string | null
  endDate: string | null
  leaveStart: string | null
  leaveEnd: string | null
  statusChangedAt: string | null
  statusReason: string | null
  createdAt: string
  updatedAt: string
  primary: boolean
  primaryReason: string | null
  integrityWarnings: string[]
}

export interface LcOperationsLearningMedia {
  mediaId: string
  kind: 'AUDIO' | 'IMAGE'
  componentProgressId: number
  slot: number | null
  fileName: string | null
  uploadedAt: string | null
}

export interface LcOperationsLearning {
  library: {
    tree: Array<{
      curriculumId: number
      title: string
      chapterCount: number
      completedChapterCount: number
      lessonCount: number
      completedLessonCount: number
      courses: Array<{
        courseId: number
        title: string
        modules: Array<{
          moduleId: number
          title: string
          lessons: Array<{
            lessonId: number
            title: string
            status: string
            reviewCount: number
            latestCorrectRate: number | null
          }>
        }>
      }>
    }>
  }
  lessonDetail: {
    lesson: { lessonId: number; title: string; chapterLabel: string; firstCorrectRate: number | null; latestCorrectRate: number | null }
    initialAttempt: {
      status: string
      startedAt: string
      completedAt: string | null
      elapsedSeconds: number
      workbookGradingMode: string | null
      components: Array<{
        componentProgressId: number
        type: string
        label: string
        status: string
        elapsedSeconds: number | null
        video?: { watchPercent: number; watchTimeSeconds: number }
        quiz?: { correctRate: number; attemptCount: number; questions: Array<{ quizId: string; isCorrect: boolean | null; studentAnswer: unknown; correctAnswer: unknown; partialCredit: number | null }> }
      }>
    }
    reviews: Array<{ sessionNumber: number; startedAt: string; completedAt: string | null }>
  } | null
  media: LcOperationsLearningMedia[]
}

export interface LcOperationsSession {
  attendanceId: number
  date: string
  status: string
  checkInAt: string | null
  checkOutAt: string | null
  coachingNote: string | null
  coachingNoteVersion: number
  growthAlertShared: boolean
  growthAlertCreatedAt: string | null
  growthAlertExpiresAt: string | null
}

export interface LcOperationsSessions {
  items: LcOperationsSession[]
  nextCursor: string | null
}

export interface LcOperationsEntitlement {
  entitlementId: number
  level: string
  courseGroup: string | null
  contentId: number | null
  stage: string | null
  grantedLessons: number
  carriedOver: number
  usedLessons: number
  remainingLessons: number
  periodStart: string | null
  periodEnd: string | null
  graceUntil: string | null
  paidAt: string | null
  source: string
  status: string
  contentFeeGate: string | null
  adminGrantExpiresOn: string | null
  revokedAt: string | null
  revokeReason: string | null
  createdAt: string
  updatedAt: string
}

export interface LcOperationsTextbook {
  requestId: number
  enrollmentId: number | null
  entitlementId: number | null
  printBookId: number
  sourceBookId: number | null
  triggerLessonId: number | null
  requestReason: string
  status: string
  inGracePeriod: boolean
  requestedBy: number
  requestedAt: string
  statusChangedBy: number | null
  statusChangedAt: string | null
  memo: string | null
  outboundOutDate: string | null
  receiverName: string | null
  receiverAddress: string | null
  receiverPhone: string | null
  createdAt: string
  updatedAt: string
}

export interface LcOperationsDiagnostic {
  assignment: {
    id: string
    userId: number
    status: string
    aiqSolved: number
    aiqTotal: number
    roundNo: number
    levelGraded: boolean
  }
  result: {
    status: string
    aiq: { correct: number; total: number; items: Array<{ seq: number; quizId: string; isCorrect: boolean | null }> }
    level: { correct: number; total: number; score: number; items: Array<{ seq: number; quizId: string; isCorrect: boolean }> } | null
  }
  representative: boolean
}

export interface LcOperationsMediaAccess {
  mediaId: string
  state: 'AVAILABLE' | 'METADATA_ONLY' | 'SIGNING_FAILED'
  signedUrl: string | null
  expiresAt: string | null
}

function requireData<T>(response: ApiResponse<T>, message: string): T {
  if (response.data === undefined) throw new Error(message)
  return response.data
}

export const lcOperationsInspectionApi = {
  async getSummary(organizationId: number): Promise<LcOperationsSummary> {
    const response = await apiClient.get<ApiResponse<LcOperationsSummary>>(
      `/admin/lc/organizations/${organizationId}/operations/summary`,
    )
    return requireData(response.data, 'Failed to load LC operations summary')
  },

  async getClassGroups(
    organizationId: number,
    query: LcOperationsClassGroupQuery,
  ): Promise<PageResponse<LcOperationsClassGroup>> {
    const response = await apiClient.get<ApiResponse<PageResponse<LcOperationsClassGroup>>>(
      `/admin/lc/organizations/${organizationId}/class-groups`,
      { params: query },
    )
    return requireData(response.data, 'Failed to load LC class groups')
  },

  async getRoster(
    organizationId: number,
    timeslotId: number,
    includeHistorical: boolean,
  ): Promise<LcOperationsRosterItem[]> {
    const response = await apiClient.get<ApiResponse<LcOperationsRosterItem[]>>(
      `/admin/lc/organizations/${organizationId}/class-groups/${timeslotId}/roster`,
      { params: { includeHistorical } },
    )
    return requireData(response.data, 'Failed to load LC class roster')
  },

  async getLearners(
    organizationId: number,
    query: LcOperationsLearnerQuery,
  ): Promise<LcOperationsLearnerPage> {
    const response = await apiClient.get<ApiResponse<LcOperationsLearnerPage>>(
      `/admin/lc/organizations/${organizationId}/learners`,
      { params: query },
    )
    return requireData(response.data, 'Failed to load LC learners')
  },

  async getLearnerOverview(
    organizationId: number,
    userId: number,
  ): Promise<LcOperationsLearnerOverview> {
    const response = await apiClient.get<ApiResponse<LcOperationsLearnerOverview>>(
      `/admin/lc/organizations/${organizationId}/learners/${userId}/overview`,
    )
    return requireData(response.data, 'Failed to load LC learner overview')
  },

  async getLearnerEnrollments(
    organizationId: number,
    userId: number,
  ): Promise<LcOperationsEnrollment[]> {
    const response = await apiClient.get<ApiResponse<LcOperationsEnrollment[]>>(
      `/admin/lc/organizations/${organizationId}/learners/${userId}/enrollments`,
    )
    return requireData(response.data, 'Failed to load LC learner enrollments')
  },

  async getLearnerLearning(
    organizationId: number,
    userId: number,
    lesson?: { lessonId: number; courseId: number; moduleId: number },
  ): Promise<LcOperationsLearning> {
    const response = await apiClient.get<ApiResponse<LcOperationsLearning>>(
      `/admin/lc/organizations/${organizationId}/learners/${userId}/learning`,
      { params: lesson ?? {} },
    )
    return requireData(response.data, 'Failed to load LC learner learning data')
  },

  async getLearnerSessions(organizationId: number, userId: number): Promise<LcOperationsSessions> {
    const response = await apiClient.get<ApiResponse<LcOperationsSessions>>(
      `/admin/lc/organizations/${organizationId}/learners/${userId}/sessions`,
      { params: { limit: 100 } },
    )
    return requireData(response.data, 'Failed to load LC learner sessions')
  },

  async getLearnerEntitlements(organizationId: number, userId: number): Promise<LcOperationsEntitlement[]> {
    const response = await apiClient.get<ApiResponse<LcOperationsEntitlement[]>>(
      `/admin/lc/organizations/${organizationId}/learners/${userId}/entitlements`,
    )
    return requireData(response.data, 'Failed to load LC learner entitlements')
  },

  async getLearnerTextbooks(organizationId: number, userId: number): Promise<LcOperationsTextbook[]> {
    const response = await apiClient.get<ApiResponse<LcOperationsTextbook[]>>(
      `/admin/lc/organizations/${organizationId}/learners/${userId}/textbooks`,
    )
    return requireData(response.data, 'Failed to load LC learner textbooks')
  },

  async getLearnerDiagnostics(organizationId: number, userId: number): Promise<LcOperationsDiagnostic[]> {
    const response = await apiClient.get<ApiResponse<LcOperationsDiagnostic[]>>(
      `/admin/lc/organizations/${organizationId}/learners/${userId}/diagnostics`,
    )
    return requireData(response.data, 'Failed to load LC learner diagnostics')
  },

  async getLearningMedia(organizationId: number, userId: number, mediaId: string): Promise<LcOperationsMediaAccess> {
    const response = await apiClient.get<ApiResponse<LcOperationsMediaAccess>>(
      `/admin/lc/organizations/${organizationId}/learners/${userId}/learning/media/${encodeURIComponent(mediaId)}`,
    )
    return requireData(response.data, 'Failed to load LC learner media')
  },
}
